/**
 * draftLabels — copy for drafts in the send wizard: the "due" clock on each
 * `DraftCard` and the skip reasons of an `approve_drafts` operation (ruling 46's
 * list, distinct from outreachSendCopy's SKIP_LABELS, which is the first-touch
 * batch's own vocabulary — the two operations don't share reasons).
 */
import type { OutreachDraft, OutreachDraftKind } from '@discovr/types';
import { bodyOverCap, subjectOverCap } from './draftCaps';
import { OPERATION_STALL_COPY } from './operationOutcomes';
import { codeOf, detailsOf, errorCopy } from './sendLabels';
import { unfilledPlaceholder } from './unfilledPlaceholder';
const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** "due in 2 h" | "due Thu 09:00" | "overdue" | "due soon" — the last is the
 *  fallback for a missing `scheduledFor`. Ruling 44: an `awaiting_approval`
 *  draft's `scheduled_for` is NULL until it's approved, so a real drafts-queue
 *  row commonly has no value here yet; this degrades gracefully rather than
 *  assuming one. Under a day away renders a relative count; a day or more
 *  away renders a weekday + time so the reader isn't stuck computing "in 61 h". */
export function draftDueLabel(scheduledFor: string | undefined, now: Date = new Date()): string {
  if (!scheduledFor) return 'due soon';
  const target = new Date(scheduledFor);
  const diffMs = target.getTime() - now.getTime();
  if (diffMs < 0) return 'overdue';
  if (diffMs < HOUR_MS) return `due in ${Math.max(1, Math.round(diffMs / MINUTE_MS))} min`;
  if (diffMs < DAY_MS) return `due in ${Math.round(diffMs / HOUR_MS)} h`;
  const weekday = target.toLocaleDateString('en-US', { weekday: 'short' });
  const time = target.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `due ${weekday} ${time}`;
}

export const SUBJECT_MISSING_SKIP = 'Skipped – subject missing';
export const BODY_MISSING_SKIP = 'Skipped – message body missing';

/** approve_drafts skip vocabulary; `replied` vs `cancelled` vs `already_approved` per Ruling 32 (G5). */
export const DRAFT_SKIP_LABELS: Record<string, string> = {
  already_approved: 'Already approved',
  replied: 'Replied before approval',
  cancelled: 'Draft was cancelled',
  closed: 'Conversation closed',
  removed: 'Removed from campaign',
  suppressed: 'Do not contact',
  campaign_paused: 'Campaign paused',
  mailbox_unavailable: 'Mailbox unavailable',
  stale_draft: 'A reply arrived first',
  not_in_campaign: 'Not in this campaign',
  already_contacted: 'Already contacted',
  no_contact: 'No email on file',
  on_hold: 'On hold',
  not_from_wizard: 'Not drafted in this Bulk send',
  operation_stalled: OPERATION_STALL_COPY,
  stalled: OPERATION_STALL_COPY,
  subject_missing: SUBJECT_MISSING_SKIP,
  body_missing: BODY_MISSING_SKIP,
};

/** Shown as their own "Skipped – …" rows, not under the "N skipped" line. */
export const MISSING_TEXT_SKIP_REASONS = ['subject_missing', 'body_missing'] as const;

/** An unknown reason reads as itself rather than disappearing (mirrors
 *  outreachSendCopy's skipLabel). */
export function draftSkipCopy(reason: string): string {
  return (Object.hasOwn(DRAFT_SKIP_LABELS, reason) ? DRAFT_SKIP_LABELS[reason] : undefined) ?? reason.replace(/_/g, ' ');
}

/** Owner ruling 2026-10-11: a first touch needs a subject and a body; a follow-up sends as "Re:" so its subject is optional.
 *  Worded for the edit being saved, the same words Go's 422 gets (owner 2026-10-12). */
export function draftTextErrors(kind: OutreachDraftKind, subject: string, body: string): { subject?: string; body?: string } {
  return {
    ...(kind === 'initial' && subject.trim() === '' ? { subject: errorCopy('DRAFT_SUBJECT_REQUIRED', undefined, 'save') } : {}),
    ...(body.trim() === '' ? { body: errorCopy('DRAFT_BODY_REQUIRED', undefined, 'save') } : {}),
  };
}

/** A stored draft that can't be sent as it stands, read before anyone opens it to edit. */
export function incompleteDraftCopy(kind: OutreachDraftKind, subject: string, body: string): string | null {
  const { subject: noSubject, body: noBody } = draftTextErrors(kind, subject, body);
  const missing = noSubject && noBody ? 'subject or message' : noSubject ? 'subject' : noBody ? 'message' : null;
  return missing ? `This email has no ${missing}. Edit it to add one before sending.` : null;
}

/** The stored draft text breaks draftTextErrors' rule. */
export function isIncompleteDraft(draft: OutreachDraft): boolean {
  return Object.keys(draftTextErrors(draft.kind, draft.message.subject ?? '', draft.message.bodyText)).length > 0;
}

/** The first placeholder still in the stored draft: the one Go's approve skips it for (has_placeholder). */
export function draftPlaceholder(draft: OutreachDraft): string | undefined {
  return unfilledPlaceholder(draft.message.subject ?? '', draft.message.bodyText);
}

export function placeholderCopy(token: string): string {
  return `Has an unfilled placeholder: ${token}`;
}

/** A stored first touch whose words are over the body limit: it can't be approved until it is shortened. */
export function isOverBodyCap(draft: OutreachDraft): boolean {
  return draft.kind === 'initial' && bodyOverCap(draft.message.bodyText);
}

/** A stored first touch whose rendered subject is over the subject limit: it can't be approved until the subject is shortened. */
export function isOverSubjectCap(draft: OutreachDraft): boolean {
  return draft.kind === 'initial' && subjectOverCap(draft.message.subject ?? '');
}

export interface DraftSaveError {
  field?: 'subject' | 'body';
  message: string;
}

const GENERIC_SAVE_ERROR = "Couldn't save this email. Try again.";

const DRAFT_FIELD_BY_CODE: Record<string, 'subject' | 'body'> = {
  DRAFT_SUBJECT_REQUIRED: 'subject',
  DRAFT_BODY_REQUIRED: 'body',
  DRAFT_BODY_TOO_LONG: 'body',
  DRAFT_SUBJECT_TOO_LONG: 'subject',
};

/** A failed draft save: the 422 field codes land on their field; anything else is one card-level line,
 *  the app's own save copy for that code when it has one, else a generic line (never the raw code). */
export function draftSaveError(error: unknown): DraftSaveError {
  const code = codeOf(error);
  if (!code || code === 'UNKNOWN') return { message: GENERIC_SAVE_ERROR };
  const copy = errorCopy(code, detailsOf(error), 'save');
  const field = Object.hasOwn(DRAFT_FIELD_BY_CODE, code) ? DRAFT_FIELD_BY_CODE[code] : undefined;
  if (field) return { field, message: copy };
  return { message: copy === code ? GENERIC_SAVE_ERROR : copy };
}
